import { PaymentMethod, User } from 'types';
import { Condition } from './common';
import type { Customer, JsonData, WithTimestamps } from './common';
import type { JobProduct, PricingCategory, ServiceType } from './product';

export type Job = {
  ID: number;
  category: PricingCategory;
  job_type: 'O' | 'R' | 'S';
  rate: 'S';
  warranty: 'N' | 'C' | 'T';
  warranty_reason_ID: number;
  customer_ID: number;
  billto_ID: number;
  datetime: string; // '2024-11-27 14:30:26';
  description: string;
  previous_description: string;
  technician_ID: number;
  appointment: string; // '2024-11-27 18:00:00';
  block: string; // wtf? '1.00';
  customer_notified: Condition;
  technician_note: string;
  technician_notified: Condition;
  technician_confirmed: Condition;
  completed: string;
  technician_report: string;
  cancelled_rate: string; // number?
  cancelled_total: string; // number?
  labour_hours_total: string; // number?
  labour_rate: string; // number?
  labour_total: string; // number?
  parts_total: string; // number?
  discount: string; // number?
  discount_percent: number;
  discount_hours: string; // number?
  sub_total: string; // number?
  tax_rate: string; // number?
  tax_applied: string; // number?
  job_total: string; // number?
  sameday_service_qty: number;
  sameday_service_rate: string; // number?
  sameday_service_total: string; // number?
  weekend_service_qty: number;
  weekend_service_rate: string; // number?
  weekend_service_total: string; // number?
  payment_method: string;
  payment_received: string; // number?
  original_job_ID: number;
  original_tech_ID: number;
  technician_datecompleted: string; // '0000-00-00 00:00:00';
  technician_invoiced: Condition;
  cancelled: string;
  cancelled_datetime: string; //'0000-00-00 00:00:00';
  discount_desc: string;
  discount_type: number;
  discount_total: number;
  sameday_service: Condition;
  weekend_service: Condition;
  item_offsite: string;
  item_eta: string; // '0000-00-00';
  item_cust_noitied: string;
};

export type JobSearchResult = Pick<
  Job,
  | 'ID'
  | 'appointment'
  | 'cancelled'
  | 'completed'
  | 'description'
  | 'item_offsite'
  | 'job_type'
  | 'technician_report'
  | 'warranty'
> & {
  booking: string;
  requested: string;
  customer_name: string;
  customer_address: string;
  customer_timezone: string;
  billto_ID: number;
  billto_name: string;
  customer: Customer;
  service_total: string;
  status: string;
  payment_method: PaymentMethod;
  job: Job;
  customer_ID?: number;
};

type ServiceTechnician = {
  ID: number;
  service_ID: number;
  technician_ID: number;
  appointment: string; //'2024-11-27 18:00:00'
  previous_appointment: string; //'2024-11-27 18:00:00'
  technician_note: string;
  technician_confirm: number; // boolean?
  invoiced: number; // boolean?
  technician_type: string; //'primary';
  created_at: string; //'2024-11-27T03:30:26.000000Z';
  updated_at: string; //'2024-11-27T03:30:26.000000Z';
  technician: { name: string; ID: number; mobile: string[] };
};

export type Service = {
  ID: number;
  customer_ID: number;
  billto_ID: number;
  status: 'open' | string;
  service_type_ID: ServiceType;
  service_description: string;
  previous_service_description: string;
  payment_received: string; // number?
  tax_rate: string; // number?
  tax_applied: string; // number?
  discount: string; // number?
  discount_percent: number;
  discount_hours: string; // number?
  discount_total: number;
  parts_total: string; // number?
  sub_total: string; // number?
  service_total: string; // number?
  completion_date: string; // '0000-00-00 00:00:00';
  user_ID: number;
  source: 'legacy' | 'bmsv3';
  created_at: string; // '2024-11-27T03:30:26.000000Z';
  created_by: User;
  updated_at: string; //'2024-11-27T04:31:01.000000Z';
  customer: Customer;
  products: Array<{ product: JobProduct; service_technicians: ServiceTechnician[] }>;
  discounts: JsonData;
  service_parts: JsonData;
  attributes: JsonData;
  support_legacy: boolean;
  job?: Job;
  jobs_labour?: JsonData;
  labour_rate_inc_gst: number;
  billto_name: string;
  payment_method: PaymentMethod;
  service_appointment: string; // '0000-00-00 00:00:00'
};

export type ServiceInvoice = {
  ID: number;
  customer_ID: number;
  date: string; // '2024-12-04'
  tax_rate: string;
  tax_applied: string;
  sub_total: string;
  total: string;
  payment_received: string;
  payment_method: PaymentMethod;
};

export type ServiceReceipt = {
  ID: number;
  customer_ID: number;
  invoice_ID: number;
  date: string; // '2024-12-05 10:46:50'
  postal_name: string;
  postal_attention: string;
  postal_address_line1: string;
  postal_address_line2: string;
  postal_suburb: string;
  postal_state: string;
  postal_postcode: string;
  type: PaymentMethod;
  cardname: string;
  txnsrc: string;
  ref: string;
  amount: string;
  datechecked: string;
  datetime_updated: string;
  userwhochecked: number;
  checked: string;
  reversed: string;
  reversed_note: string;
  reversed_ID: number;
  createdat: string;
  createdby: number;
  reversedat: string;
  reversedby: number;
  surcharge: string;
  confirm_notes: string;
  settlementDate: string;
};

export enum ReschedulingSteps {
  SelectReason = 'select-reason',
  ReviewDetails = 'review-details',
  EditDetails = 'update-details',
  SelectSlotSimple = 'select-slot-simple',
  SelectSlot = 'select-slot',
  SendNotification = 'send-notification',
  ReviewSummary = 'summary',
}

export type RescheduleReason = { ID: number; description: string; category: 'T' | 'C' | 'O' | 'R' };

export type ServiceProduct = WithTimestamps<{
  id: number;
  productId: number;
  quantity: number;
  price: number;
  completionDate: Date | null;
  createdBy: number;
  // NOTE: This is a specific structure for this endpoint at the moment and
  //       should be generalised once the backend returns this consistently
  product: WithTimestamps<{
    description: string;
    id: number;
    name: string;
    status: string;
    subTotal: number;
    taxApplied: number;
    taxRate: number;
    total: number;
  }> | null;
}>;
